import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import {
  OutputType as TablesOutputType,
  TableInfo,
} from '../endpoints/sites/database-tables_GET.schema';
import {
  OutputType as SchemaOutputType,
  ColumnInfo,
  IndexInfo,
} from '../endpoints/sites/database-schema_GET.schema';
import {
  OutputType as QueryOutputType,
  QueryResultRow,
} from '../endpoints/sites/database-query_POST.schema';

const API_BASE = '/_api';

async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
  });

  const text = await response.text();
  const data = superjson.parse(text) as T & { error?: string };

  if (!response.ok || data.error) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

/**
 * Hook to fetch tables for a site's database
 */
export function useDatabaseTables(siteId: number | null) {
  return useQuery({
    queryKey: ['database-tables', siteId],
    queryFn: () => fetchApi<TablesOutputType>(`${API_BASE}/sites/database-tables?siteId=${siteId}`),
    enabled: !!siteId,
  });
}

/**
 * Hook to fetch schema for a specific table
 */
export function useTableSchema(siteId: number | null, tableName: string | null) {
  return useQuery({
    queryKey: ['database-schema', siteId, tableName],
    queryFn: () =>
      fetchApi<SchemaOutputType>(
        `${API_BASE}/sites/database-schema?siteId=${siteId}&tableName=${encodeURIComponent(tableName || '')}`,
      ),
    enabled: !!siteId && !!tableName,
  });
}

/**
 * Hook to execute a SQL query
 */
export function useExecuteQuery() {
  return useMutation({
    mutationFn: (data: { siteId: number; sql: string; params?: unknown[] }) =>
      fetchApi<QueryOutputType>(`${API_BASE}/sites/database-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(data),
      }),
  });
}

/**
 * Hook to refresh table data after mutation
 */
export function useRefreshTables() {
  const queryClient = useQueryClient();

  return (siteId: number) => {
    queryClient.invalidateQueries({ queryKey: ['database-tables', siteId] });
  };
}

export type { TableInfo, ColumnInfo, IndexInfo, QueryResultRow };
